import {
  Bike,
  Bus,
  Car,
  Flag,
  Flame,
  Heart,
  Home,
  MapPin,
  Plane,
  Repeat2,
  Scale,
  Shield,
  Ship,
  Stethoscope,
} from "lucide-react";

function EndPoint({ align = "left", icon: Icon, eyebrow, title, tone = "emerald" }) {
  const tones = {
    emerald: "bg-emerald-600 text-white",
    orange: "bg-orange-500 text-white",
    gray: "bg-gray-800 text-white",
  };

  return (
    <div
      className={`flex min-w-0 max-w-[44%] flex-col ${
        align === "right" ? "items-end text-right" : "items-start text-left"
      }`}
    >
      <span className={`grid h-8 w-8 place-items-center rounded-full ${tones[tone]}`}>
        <Icon size={14} aria-hidden="true" />
      </span>
      <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {eyebrow}
      </p>
      <p className="text-sm font-semibold leading-tight text-gray-900">{title}</p>
    </div>
  );
}

function Connector({ icon: Icon, caption }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center px-1 pt-1">
      <div className="flex w-full items-center">
        <span className="h-px flex-1 border-t-2 border-dashed border-gray-300" aria-hidden="true" />
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-orange-600 ring-1 ring-orange-200">
          <Icon size={14} aria-hidden="true" />
        </span>
        <span className="h-px flex-1 border-t-2 border-dashed border-gray-300" aria-hidden="true" />
      </div>
      {caption ? (
        <p className="mt-1 max-w-[7.5rem] text-center text-[10px] font-medium leading-tight text-gray-500">
          {caption}
        </p>
      ) : null}
    </div>
  );
}

function Journey({ from, to, icon, caption }) {
  return (
    <div className="flex items-start justify-between gap-1">
      <EndPoint {...from} />
      <Connector icon={icon} caption={caption} />
      <EndPoint align="right" tone="orange" {...to} />
    </div>
  );
}

function formatGeography(geography) {
  if (geography === "both") return "Regional + Intl";
  if (!geography) return "Travel cover";
  return geography.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function generalAsset(policyType) {
  const map = {
    health_insurance: { icon: Stethoscope, title: "Your health" },
    home_insurance: { icon: Home, title: "Your home" },
    accident_insurance: { icon: Shield, title: "Accident cover" },
    fire_insurance: { icon: Flame, title: "Fire cover" },
    marine_insurance: { icon: Ship, title: "Marine cover" },
    liability_insurance: { icon: Scale, title: "Liability cover" },
  };
  return map[policyType] || { icon: Shield, title: "Your cover" };
}

export default function PolicyCoverVisual({
  policyType,
  insuranceType,
  destination,
  duration,
  geography,
  coverLabel,
  holderName,
}) {
  const isLife = insuranceType === "life_insurance";
  const isGeneral = insuranceType === "general_insurance";
  const isTravelPackage = insuranceType === "travel_insurance" && policyType === "travel_insurance";
  const destinationLabel = destination || coverLabel || "Selected destination";

  let title = "Cover at a glance";
  let description = "";
  let visual = null;

  if (policyType === "intercity_travel") {
    title = "Your journey";
    description = `Greater Accra to ${destinationLabel}`;
    visual = (
      <Journey
        from={{ icon: MapPin, eyebrow: "Departure", title: "Greater Accra" }}
        to={{ icon: Flag, eyebrow: "Destination", title: destinationLabel }}
        icon={Bus}
        caption={duration || "Intercity trip"}
      />
    );
  } else if (policyType === "daily_commuter") {
    title = "Daily commute";
    description = "Same-day trips in Greater Accra";
    visual = (
      <Journey
        from={{ icon: MapPin, eyebrow: "Start", title: "Greater Accra" }}
        to={{ icon: Repeat2, eyebrow: "Return", title: "Back the same day" }}
        icon={Bus}
        caption={duration || "Daily cover"}
      />
    );
  } else if (policyType === "okada_rider") {
    title = destination ? "Your ride" : "On-road cover";
    description = destination ? `Covered toward ${destinationLabel}` : "Motor rider protection while you ride";
    visual = (
      <Journey
        from={{ icon: Bike, eyebrow: "Rider", title: "On the road" }}
        to={{
          icon: destination ? Flag : Shield,
          eyebrow: destination ? "Destination" : "Protected",
          title: destination ? destinationLabel : duration || "Ride cover",
        }}
        icon={Bike}
        caption={duration || "Motor rider"}
      />
    );
  } else if (policyType === "taxi_and_bolt") {
    title = "Driver cover";
    description = "Protection for your working hours on the road";
    visual = (
      <Journey
        from={{ icon: Car, eyebrow: "Shift start", title: "On duty" }}
        to={{ icon: Shield, eyebrow: "Covered until", title: duration || "End of shift" }}
        icon={Car}
        caption="Taxi & Bolt"
      />
    );
  } else if (isTravelPackage) {
    title = "Travel cover";
    description = `From Ghana across ${formatGeography(geography).toLowerCase()} trips`;
    visual = (
      <Journey
        from={{ icon: MapPin, eyebrow: "From", title: "Ghana" }}
        to={{ icon: Plane, eyebrow: "Covers", title: formatGeography(geography) }}
        icon={Plane}
        caption={coverLabel || duration || "Travel"}
      />
    );
  } else if (isLife) {
    title = "Life cover";
    description = holderName
      ? `${holderName} is protected for the selected term`
      : "Protection for the selected term";
    visual = (
      <Journey
        from={{ icon: Heart, eyebrow: "Starts", title: "Today" }}
        to={{ icon: Shield, eyebrow: "Covered for", title: duration || coverLabel || "Selected term" }}
        icon={Heart}
        caption={holderName || "Life insurance"}
      />
    );
  } else if (isGeneral) {
    const asset = generalAsset(policyType);
    title = "What's protected";
    description = coverLabel || "General insurance protection";
    visual = (
      <Journey
        from={{ icon: asset.icon, eyebrow: "Asset", title: asset.title }}
        to={{ icon: Shield, eyebrow: "Protected for", title: duration || coverLabel || "Selected term" }}
        icon={Shield}
        caption={duration || "Insured"}
      />
    );
  } else {
    title = "Your cover";
    visual = (
      <Journey
        from={{ icon: Shield, eyebrow: "Policy", title: coverLabel || "Selected cover" }}
        to={{ icon: Flag, eyebrow: "Protected", title: duration || "After payment" }}
        icon={Shield}
        caption="TripSecure"
      />
    );
  }

  return (
    <section className="mt-4 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100" aria-label={description || title}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      {description ? <p className="mt-0.5 text-xs text-gray-600">{description}</p> : null}
      <div className="mt-3">{visual}</div>
    </section>
  );
}
